import { useState, useEffect, createContext, useContext } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";

// Pages
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import VerifyEmailPage from "./pages/VerifyEmailPage";
import FaceAuthPage from "./pages/FaceAuthPage";
import WhatsAppRegisterPage from "./pages/WhatsAppRegisterPage";
import LineRegisterPage from "./pages/LineRegisterPage";
import StaffDashboard from "./pages/StaffDashboard";
import AdminDashboard from "./pages/AdminDashboard";
import EmployeesPage from "./pages/EmployeesPage";
import AttendancePage from "./pages/AttendancePage";
import SettingsPage from "./pages/SettingsPage";
import SubscriptionPage from "./pages/SubscriptionPage";
import SuperAdminPage from "./pages/SuperAdminPage";
import SitePage from "./pages/SitePage";
import QuoteSimulatorPage from "./pages/QuoteSimulatorPage";
import GuidePage from "./pages/GuidePage";
import AdminManualPage from "./pages/AdminManualPage";
import WhatsAppSettingsPage from "./pages/WhatsAppSettingsPage";
import LineSettingsPage from "./pages/LineSettingsPage";
import AdminLayout from "./components/AdminLayout";
import SaaSAdminPage from "./pages/SaaSAdminPage";
import SaaSAdminLoginPage from "./pages/SaaSAdminLoginPage";
import ShiftPage from "./pages/ShiftPage";
import MasterSettingsPage from "./pages/MasterSettingsPage";
import EmployeeSalaryPage from "./pages/EmployeeSalaryPage";
import SalaryPage from "./pages/SalaryPage";
import WageLedgerPage from "./pages/WageLedgerPage";
import YearEndAdjustmentPage from "./pages/YearEndAdjustmentPage";
import StaffLeavePage from "./pages/StaffLeavePage";

// PWA Components
import PWAInstallPrompt from "./components/PWAInstallPrompt";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// System Logo Context
const LogoContext = createContext();
export const useLogo = () => useContext(LogoContext);

const LogoProvider = ({ children }) => {
  const [logoUrl, setLogoUrl] = useState(null);
  const [logoLoading, setLogoLoading] = useState(true);

  useEffect(() => {
    const fetchLogo = async () => {
      try {
        const res = await axios.get(`${API}/public/logo`);
        if (res.data.logo_url && res.data.logo_url.length > 0) {
          setLogoUrl(res.data.logo_url);
        } else {
          setLogoUrl("/logo.png");
        }
      } catch (error) {
        console.log("Using default logo");
        setLogoUrl("/logo.png");
      } finally {
        setLogoLoading(false);
      }
    };
    fetchLogo();
  }, []);

  const effectiveLogoUrl = logoUrl || "/logo.png";

  return (
    <LogoContext.Provider value={{ logoUrl: effectiveLogoUrl, logoLoading }}>
      {children}
    </LogoContext.Provider>
  );
};

// Translations
export const LANGUAGES = [
  { code: "ja", name: "日本語", countryCode: "jp" },
  { code: "en", name: "English", countryCode: "us" },
  { code: "pt", name: "Português", countryCode: "br" },
  { code: "vi", name: "Tiếng Việt", countryCode: "vn" },
  { code: "tl", name: "Tagalog", countryCode: "ph" },
  { code: "es", name: "Español", countryCode: "es" },
];

// Translation texts for all pages
export const TRANSLATIONS = {
  ja: {
    // Landing Page
    landingTitle: "PRC勤怠",
    landingSubtitle: "シンプルで使いやすい勤怠管理システム",
    landingDescription: "顔認証で簡単打刻。多言語対応で外国人スタッフも安心。",
    getStarted: "今すぐ始める",
    adminLogin: "管理者ログイン",
    faceAuth: "顔認証で打刻",
    features: "機能紹介",
    featureAttendance: "勤怠管理",
    featureAttendanceDesc: "出退勤の記録、残業時間の自動計算",
    featureEmployee: "従業員管理",
    featureEmployeeDesc: "従業員情報の一元管理、有給管理",
    featureReport: "レポート",
    featureReportDesc: "月次レポート、タイムカード出力",
    featureSecurity: "セキュリティ",
    featureSecurityDesc: "顔認証、データ暗号化",
    multiLanguage: "多言語対応",
    multiLanguageDesc: "6言語に対応。外国人スタッフも母国語で利用可能",
    supportedLanguages: "対応言語",
    // Login Page
    login: "ログイン",
    email: "メールアドレス",
    password: "パスワード",
    loginButton: "ログイン",
    forgotPassword: "パスワードを忘れた方",
    noAccount: "アカウントをお持ちでない方",
    register: "新規登録",
    // Face Auth Page
    faceAuthTitle: "QRコード打刻",
    enterCompanyCode: "企業コードを入力",
    companyCode: "企業コード",
    startFaceAuth: "認証を開始",
    clockIn: "出勤",
    clockOut: "退勤",
    detecting: "顔を検出中...",
    recognizing: "認証中...",
    success: "認証成功",
    failed: "認証失敗",
    tryAgain: "もう一度お試しください",
    cameraError: "カメラにアクセスできません",
    // Common
    loading: "読み込み中...",
    error: "エラー",
    save: "保存",
    cancel: "キャンセル",
    delete: "削除",
    edit: "編集",
    add: "追加",
    search: "検索",
    back: "戻る",
    next: "次へ",
    confirm: "確認",
    close: "閉じる",
    // Greetings
    goodMorning: "おはようございます",
    goodAfternoon: "お疲れ様です",
    goodEvening: "お疲れ様でした",
    thankYou: "ありがとうございました",
  },
  en: {
    // Landing Page
    landingTitle: "PRC Attendance",
    landingSubtitle: "Simple and Easy Attendance Management System",
    landingDescription: "Easy clock-in with face recognition. Multi-language support for international staff.",
    getStarted: "Get Started",
    adminLogin: "Admin Login",
    faceAuth: "Face Recognition Clock-in",
    features: "Features",
    featureAttendance: "Attendance Management",
    featureAttendanceDesc: "Record clock-in/out, automatic overtime calculation",
    featureEmployee: "Employee Management",
    featureEmployeeDesc: "Centralized employee info, leave management",
    featureReport: "Reports",
    featureReportDesc: "Monthly reports, timecard export",
    featureSecurity: "Security",
    featureSecurityDesc: "Face recognition, data encryption",
    multiLanguage: "Multi-language Support",
    multiLanguageDesc: "Available in 6 languages. Staff can use in their native language",
    supportedLanguages: "Supported Languages",
    // Login Page
    login: "Login",
    email: "Email",
    password: "Password",
    loginButton: "Login",
    forgotPassword: "Forgot password?",
    noAccount: "Don't have an account?",
    register: "Register",
    // Face Auth Page
    faceAuthTitle: "QR Code Clock-in",
    enterCompanyCode: "Enter Company Code",
    companyCode: "Company Code",
    startFaceAuth: "Start Authentication",
    clockIn: "Clock In",
    clockOut: "Clock Out",
    detecting: "Detecting face...",
    recognizing: "Recognizing...",
    success: "Success",
    failed: "Failed",
    tryAgain: "Please try again",
    cameraError: "Cannot access camera",
    // Common
    loading: "Loading...",
    error: "Error",
    save: "Save",
    cancel: "Cancel",
    delete: "Delete",
    edit: "Edit",
    add: "Add",
    search: "Search",
    back: "Back",
    next: "Next",
    confirm: "Confirm",
    close: "Close",
    // Greetings
    goodMorning: "Good morning",
    goodAfternoon: "Good afternoon",
    goodEvening: "Good evening",
    thankYou: "Thank you",
  },
  pt: {
    // Landing Page
    landingTitle: "PRC Ponto",
    landingSubtitle: "Sistema de Controle de Ponto Simples e Fácil",
    landingDescription: "Registro fácil com reconhecimento facial. Suporte multilíngue para funcionários internacionais.",
    getStarted: "Começar Agora",
    adminLogin: "Login Admin",
    faceAuth: "Registro por Reconhecimento Facial",
    features: "Recursos",
    featureAttendance: "Controle de Ponto",
    featureAttendanceDesc: "Registro de entrada/saída, cálculo automático de horas extras",
    featureEmployee: "Gestão de Funcionários",
    featureEmployeeDesc: "Informações centralizadas, gestão de férias",
    featureReport: "Relatórios",
    featureReportDesc: "Relatórios mensais, exportação de cartão de ponto",
    featureSecurity: "Segurança",
    featureSecurityDesc: "Reconhecimento facial, criptografia de dados",
    multiLanguage: "Suporte Multilíngue",
    multiLanguageDesc: "Disponível em 6 idiomas. Funcionários podem usar em seu idioma nativo",
    supportedLanguages: "Idiomas Suportados",
    // Login Page
    login: "Entrar",
    email: "E-mail",
    password: "Senha",
    loginButton: "Entrar",
    forgotPassword: "Esqueceu a senha?",
    noAccount: "Não tem conta?",
    register: "Registrar",
    // Face Auth Page
    faceAuthTitle: "Registro por Código QR",
    enterCompanyCode: "Digite o Código da Empresa",
    companyCode: "Código da Empresa",
    startFaceAuth: "Iniciar Autenticação",
    clockIn: "Entrada",
    clockOut: "Saída",
    detecting: "Detectando rosto...",
    recognizing: "Reconhecendo...",
    success: "Sucesso",
    failed: "Falhou",
    tryAgain: "Por favor, tente novamente",
    cameraError: "Não foi possível acessar a câmera",
    // Common
    loading: "Carregando...",
    error: "Erro",
    save: "Salvar",
    cancel: "Cancelar",
    delete: "Excluir",
    edit: "Editar",
    add: "Adicionar",
    search: "Buscar",
    back: "Voltar",
    next: "Próximo",
    confirm: "Confirmar",
    close: "Fechar",
    // Greetings
    goodMorning: "Bom dia",
    goodAfternoon: "Boa tarde",
    goodEvening: "Boa noite",
    thankYou: "Obrigado",
  },
  vi: {
    // Landing Page
    landingTitle: "PRC Chấm Công",
    landingSubtitle: "Hệ thống quản lý chấm công đơn giản và dễ sử dụng",
    landingDescription: "Chấm công dễ dàng bằng nhận diện khuôn mặt. Hỗ trợ đa ngôn ngữ cho nhân viên quốc tế.",
    getStarted: "Bắt Đầu Ngay",
    adminLogin: "Đăng Nhập Quản Trị",
    faceAuth: "Chấm Công Nhận Diện Khuôn Mặt",
    features: "Tính Năng",
    featureAttendance: "Quản Lý Chấm Công",
    featureAttendanceDesc: "Ghi nhận vào/ra, tính giờ làm thêm tự động",
    featureEmployee: "Quản Lý Nhân Viên",
    featureEmployeeDesc: "Thông tin nhân viên tập trung, quản lý nghỉ phép",
    featureReport: "Báo Cáo",
    featureReportDesc: "Báo cáo hàng tháng, xuất bảng chấm công",
    featureSecurity: "Bảo Mật",
    featureSecurityDesc: "Nhận diện khuôn mặt, mã hóa dữ liệu",
    multiLanguage: "Hỗ Trợ Đa Ngôn Ngữ",
    multiLanguageDesc: "Có sẵn 6 ngôn ngữ. Nhân viên có thể sử dụng ngôn ngữ mẹ đẻ",
    supportedLanguages: "Ngôn Ngữ Hỗ Trợ",
    // Login Page
    login: "Đăng Nhập",
    email: "Email",
    password: "Mật khẩu",
    loginButton: "Đăng Nhập",
    forgotPassword: "Quên mật khẩu?",
    noAccount: "Chưa có tài khoản?",
    register: "Đăng Ký",
    // Face Auth Page
    faceAuthTitle: "Chấm Công Bằng Mã QR",
    enterCompanyCode: "Nhập Mã Công Ty",
    companyCode: "Mã Công Ty",
    startFaceAuth: "Bắt Đầu Xác Thực",
    clockIn: "Vào Ca",
    clockOut: "Tan Ca",
    detecting: "Đang phát hiện khuôn mặt...",
    recognizing: "Đang nhận diện...",
    success: "Thành công",
    failed: "Thất bại",
    tryAgain: "Vui lòng thử lại",
    cameraError: "Không thể truy cập camera",
    // Common
    loading: "Đang tải...",
    error: "Lỗi",
    save: "Lưu",
    cancel: "Hủy",
    delete: "Xóa",
    edit: "Sửa",
    add: "Thêm",
    search: "Tìm kiếm",
    back: "Quay lại",
    next: "Tiếp theo",
    confirm: "Xác nhận",
    close: "Đóng",
    // Greetings
    goodMorning: "Chào buổi sáng",
    goodAfternoon: "Chào buổi chiều",
    goodEvening: "Chào buổi tối",
    thankYou: "Cảm ơn bạn",
  },
  tl: {
    // Landing Page
    landingTitle: "PRC Attendance",
    landingSubtitle: "Simple at Madaling Gamitin na Sistema ng Attendance",
    landingDescription: "Madaling mag-clock in gamit ang face recognition. Multi-language support para sa mga international staff.",
    getStarted: "Magsimula Na",
    adminLogin: "Admin Login",
    faceAuth: "Face Recognition Clock-in",
    features: "Mga Feature",
    featureAttendance: "Attendance Management",
    featureAttendanceDesc: "Record ng clock-in/out, automatic overtime calculation",
    featureEmployee: "Employee Management",
    featureEmployeeDesc: "Centralized employee info, leave management",
    featureReport: "Reports",
    featureReportDesc: "Monthly reports, timecard export",
    featureSecurity: "Security",
    featureSecurityDesc: "Face recognition, data encryption",
    multiLanguage: "Multi-language Support",
    multiLanguageDesc: "Available sa 6 na wika. Pwedeng gamitin ng staff sa kanilang sariling wika",
    supportedLanguages: "Mga Sinusuportahang Wika",
    // Login Page
    login: "Mag-login",
    email: "Email",
    password: "Password",
    loginButton: "Mag-login",
    forgotPassword: "Nakalimutan ang password?",
    noAccount: "Walang account?",
    register: "Mag-register",
    // Face Auth Page
    faceAuthTitle: "QR Code Clock-in",
    enterCompanyCode: "Ilagay ang Company Code",
    companyCode: "Company Code",
    startFaceAuth: "Simulan ang Authentication",
    clockIn: "Clock In",
    clockOut: "Clock Out",
    detecting: "Naghahanap ng mukha...",
    recognizing: "Kinikilala...",
    success: "Tagumpay",
    failed: "Nabigo",
    tryAgain: "Pakisubukan muli",
    cameraError: "Hindi ma-access ang camera",
    // Common
    loading: "Naglo-load...",
    error: "Error",
    save: "I-save",
    cancel: "Kanselahin",
    delete: "Tanggalin",
    edit: "I-edit",
    add: "Magdagdag",
    search: "Maghanap",
    back: "Bumalik",
    next: "Susunod",
    confirm: "Kumpirmahin",
    close: "Isara",
    // Greetings
    goodMorning: "Magandang umaga",
    goodAfternoon: "Magandang hapon",
    goodEvening: "Magandang gabi",
    thankYou: "Salamat",
  },
  es: {
    // Landing Page
    landingTitle: "PRC Asistencia",
    landingSubtitle: "Sistema de Control de Asistencia Simple y Fácil",
    landingDescription: "Fichaje fácil con reconocimiento facial. Soporte multilingüe para personal internacional.",
    getStarted: "Comenzar Ahora",
    adminLogin: "Acceso Admin",
    faceAuth: "Fichaje por Reconocimiento Facial",
    features: "Características",
    featureAttendance: "Control de Asistencia",
    featureAttendanceDesc: "Registro de entrada/salida, cálculo automático de horas extra",
    featureEmployee: "Gestión de Empleados",
    featureEmployeeDesc: "Información centralizada, gestión de vacaciones",
    featureReport: "Informes",
    featureReportDesc: "Informes mensuales, exportación de tarjeta de tiempo",
    featureSecurity: "Seguridad",
    featureSecurityDesc: "Reconocimiento facial, encriptación de datos",
    multiLanguage: "Soporte Multilingüe",
    multiLanguageDesc: "Disponible en 6 idiomas. El personal puede usar su idioma nativo",
    supportedLanguages: "Idiomas Soportados",
    // Login Page
    login: "Iniciar Sesión",
    email: "Correo Electrónico",
    password: "Contraseña",
    loginButton: "Iniciar Sesión",
    forgotPassword: "¿Olvidó su contraseña?",
    noAccount: "¿No tiene cuenta?",
    register: "Registrarse",
    // Face Auth Page
    faceAuthTitle: "Fichaje por Reconocimiento Facial",
    enterCompanyCode: "Ingrese el Código de Empresa",
    companyCode: "Código de Empresa",
    startFaceAuth: "Iniciar Reconocimiento Facial",
    clockIn: "Entrada",
    clockOut: "Salida",
    detecting: "Detectando rostro...",
    recognizing: "Reconociendo...",
    success: "Éxito",
    failed: "Fallido",
    tryAgain: "Por favor, inténtelo de nuevo",
    cameraError: "No se puede acceder a la cámara",
    // Common
    loading: "Cargando...",
    error: "Error",
    save: "Guardar",
    cancel: "Cancelar",
    delete: "Eliminar",
    edit: "Editar",
    add: "Añadir",
    search: "Buscar",
    back: "Volver",
    next: "Siguiente",
    confirm: "Confirmar",
    close: "Cerrar",
    // Greetings
    goodMorning: "Buenos días",
    goodAfternoon: "Buenas tardes",
    goodEvening: "Buenas noches",
    thankYou: "Gracias",
  },
};

// SVG Flag components - no external dependencies
const FlagSVGs = {
  jp: (size) => (
    <svg width={size} height={size * 0.67} viewBox="0 0 900 600" className="inline-block rounded-sm border border-slate-300">
      <rect fill="#fff" width="900" height="600"/>
      <circle fill="#bc002d" cx="450" cy="300" r="180"/>
    </svg>
  ),
  us: (size) => (
    <svg width={size} height={size * 0.53} viewBox="0 0 1235 650" className="inline-block rounded-sm border border-slate-300">
      <rect fill="#bf0a30" width="1235" height="650"/>
      <rect fill="#fff" y="50" width="1235" height="50"/>
      <rect fill="#fff" y="150" width="1235" height="50"/>
      <rect fill="#fff" y="250" width="1235" height="50"/>
      <rect fill="#fff" y="350" width="1235" height="50"/>
      <rect fill="#fff" y="450" width="1235" height="50"/>
      <rect fill="#fff" y="550" width="1235" height="50"/>
      <rect fill="#002868" width="494" height="350"/>
    </svg>
  ),
  br: (size) => (
    <svg width={size} height={size * 0.7} viewBox="0 0 720 504" className="inline-block rounded-sm border border-slate-300">
      <rect fill="#009c3b" width="720" height="504"/>
      <polygon fill="#ffdf00" points="360,42 668,252 360,462 52,252"/>
      <circle fill="#002776" cx="360" cy="252" r="115"/>
    </svg>
  ),
  vn: (size) => (
    <svg width={size} height={size * 0.67} viewBox="0 0 900 600" className="inline-block rounded-sm border border-slate-300">
      <rect fill="#da251d" width="900" height="600"/>
      <polygon fill="#ffff00" points="450,120 510,300 390,195 510,195 390,300"/>
    </svg>
  ),
  ph: (size) => (
    <svg width={size} height={size * 0.5} viewBox="0 0 900 450" className="inline-block rounded-sm border border-slate-300">
      <rect fill="#0038a8" width="900" height="225"/>
      <rect fill="#ce1126" y="225" width="900" height="225"/>
      <polygon fill="#fff" points="0,0 450,225 0,450"/>
      <circle fill="#fcd116" cx="150" cy="225" r="60"/>
    </svg>
  ),
  es: (size) => (
    <svg width={size} height={size * 0.67} viewBox="0 0 750 500" className="inline-block rounded-sm border border-slate-300">
      <rect fill="#aa151b" width="750" height="500"/>
      <rect fill="#f1bf00" y="125" width="750" height="250"/>
    </svg>
  ),
};

export const Flag = ({ countryCode, size = 20 }) => {
  const FlagComponent = FlagSVGs[countryCode];
  return FlagComponent ? FlagComponent(size) : <span className="inline-block w-5 h-3 bg-slate-300 rounded-sm"></span>;
};

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // ログアウト処理
  const performLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    delete axios.defaults.headers.common["Authorization"];
    setUser(null);
  };

  useEffect(() => {
    const token = localStorage.getItem("token");
    const storedUser = localStorage.getItem("user");
    
    if (token && storedUser) {
      // 自動ログアウト無効: トークンがあればそのまま使用
      setUser(JSON.parse(storedUser));
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    }
    setLoading(false);

    // 自動ログアウト無効: 401エラー時のリダイレクトを削除
    // エラーはそのまま返す（各コンポーネントで処理）
  }, []);

  const login = async (email, password) => {
    try {
      const res = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem("token", res.data.token);
      localStorage.setItem("user", JSON.stringify(res.data.user));
      axios.defaults.headers.common["Authorization"] = `Bearer ${res.data.token}`;
      setUser(res.data.user);
      return { success: true };
    } catch (error) {
      console.error("Login error:", error);
      // Check if verification is required
      const requiresVerification = error.response?.headers?.['x-requires-verification'] === 'true' ||
        error.response?.data?.detail?.includes('未認証');
      
      // ネットワークエラーの場合
      if (!error.response) {
        return { 
          success: false, 
          error: "サーバーに接続できません。インターネット接続を確認してください。",
          requiresVerification: false
        };
      }
      
      return { 
        success: false, 
        error: error.response?.data?.detail || "ログインに失敗しました",
        requiresVerification
      };
    }
  };

  const register = async (name, email, password) => {
    try {
      const res = await axios.post(`${API}/auth/register`, { name, email, password, role: "admin" });
      // Registration now requires email verification
      return { 
        success: true, 
        requiresVerification: res.data.requires_verification,
        email: res.data.email
      };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || "Registration failed" };
    }
  };

  const logout = () => {
    performLogout();
  };

  return (
    <AuthContext.Provider value={{ user, login, register, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-slate-900 dark:border-white"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

function App() {
  return (
    <LogoProvider>
      <AuthProvider>
        <BrowserRouter>
          <Toaster 
            position="top-right" 
            richColors 
            closeButton
            toastOptions={{
              style: {
                fontFamily: "'Noto Sans JP', 'Inter', sans-serif"
              }
            }}
          />
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/p/:slug" element={<SitePage />} />
            <Route path="/estimate" element={<QuoteSimulatorPage />} />
            <Route path="/guide" element={<GuidePage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/verify-email" element={<VerifyEmailPage />} />
            <Route path="/face-auth" element={<FaceAuthPage />} />
            <Route path="/wa-register" element={<WhatsAppRegisterPage />} />
            <Route path="/line-register" element={<LineRegisterPage />} />
            <Route path="/my-report/:employeeId" element={<StaffDashboard />} />
            <Route path="/staff-leave" element={<StaffLeavePage />} />
            
            {/* Admin Routes */}
            <Route path="/admin" element={
              <ProtectedRoute>
                <AdminLayout />
              </ProtectedRoute>
            }>
              <Route index element={<AdminDashboard />} />
              <Route path="employees" element={<EmployeesPage />} />
              <Route path="employees/:employeeId/salary" element={<EmployeeSalaryPage />} />
              <Route path="attendance" element={<AttendancePage />} />
              <Route path="shifts" element={<ShiftPage />} />
              <Route path="settings" element={<SettingsPage />} />
              <Route path="whatsapp" element={<WhatsAppSettingsPage />} />
              <Route path="line" element={<LineSettingsPage />} />
              <Route path="masters" element={<MasterSettingsPage />} />
              <Route path="salary" element={<SalaryPage />} />
              <Route path="wage-ledger" element={<WageLedgerPage />} />
              <Route path="year-end-adjustment" element={<YearEndAdjustmentPage />} />
              <Route path="subscription" element={<SubscriptionPage />} />
              <Route path="manual" element={<AdminManualPage />} />
              <Route path="super-admin" element={<SuperAdminPage />} />
            </Route>

            {/* SaaS Admin Routes */}
            <Route path="/saas-admin/login" element={<SaaSAdminLoginPage />} />
            <Route path="/saas-admin" element={<SaaSAdminPage />} />

            {/* Fallback */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          
          {/* PWA Install Prompt */}
          <PWAInstallPrompt />
        </BrowserRouter>
      </AuthProvider>
    </LogoProvider>
  );
}

export default App;
